import { useEffect, useRef, useState } from 'react';
import type { IconDefinition } from '../../shared/iconDefinition';
import type { ReplayReactionEvent } from '../../shared/replayReactionEvent';
import { ApiRequestError } from '../api/apiRequestError';
import { getCreatorReplayStream } from '../api/getCreatorReplayStream';
import { ReactionReplayOverlay } from '../replay/reactionReplayOverlay';
import { youtubeIframeController } from '../player/youtubeIframeController';

type PlayerHandle = Awaited<ReturnType<typeof youtubeIframeController>>;

// The embedded player on the creator dashboard, plus the replay toggle.
//
// REPLAY IS ON BY DEFAULT (Cowork022 §1, reversing BUILD-SPEC-V0 §6.3).
//
// The old rule was that a dashboard sitting open should not hold everyone's
// reactions in memory, so the stream was fetched only when the toggle was
// pressed. That argument does not survive contact: the creator is authorised to
// see this data, the charts already on the same page are built from exactly the
// same rows, and the cost of the rule was that the best thing in the product sat
// behind a button nobody knew to press.
//
// "ON" MEANS ARMED, NOT PLAYING. The overlay is ready the moment the creator
// presses play; nothing starts a video on page load. That distinction is the
// whole of Cowork022's caveat — a page that begins playing video with sound by
// itself is a different and unwelcome product. The YouTube player is created
// without an autoplay parameter, so this is true by construction rather than by
// a guard that could be edited away, and there is an assertion for it.
//
// The toggle stays, so a creator who wants the plain video can have it.
//
// This also serves the top-moments table: seekToMs is exposed upward so a row
// can drive the playhead.
export function DashboardReplayPlayer(input: {
  dashboardKey: string;
  youtubeId: string;
  icons: IconDefinition[];
  onPlayerReady: (seekToMs: (ms: number) => void) => void;
}) {
  // On by default. The fetch below fills `events`; until it lands the overlay is
  // enabled with an empty list, which draws nothing — and the scheduler emits
  // nothing on its first frame regardless, so an armed overlay over a paused
  // video at 0:00 is inert rather than merely quiet.
  const [replayOn, setReplayOn] = useState(true);
  const [events, setEvents] = useState<ReplayReactionEvent[] | null>(null);
  // Starts true because the fetch below starts immediately. Initialising it false
  // would render "Turn off reaction replay" for one frame before flipping to
  // "Loading…", which reads as a flicker on every dashboard load.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetched with the rest of the dashboard rather than on demand (Cowork022 §1).
  // If it fails, replay switches OFF rather than sitting armed and permanently
  // empty: a toggle reading "turn off" while nothing can ever appear is worse
  // than an honest error and a button that says "turn on".
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCreatorReplayStream(input.dashboardKey)
      .then((stream) => {
        if (cancelled) return;
        setEvents(stream);
      })
      .catch((caught) => {
        if (cancelled) return;
        setReplayOn(false);
        setError(caught instanceof ApiRequestError ? caught.message : 'Could not load the replay.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [input.dashboardKey]);

  const mountRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<PlayerHandle | null>(null);
  const bootedRef = useRef(false);
  const onReadyRef = useRef(input.onPlayerReady);
  onReadyRef.current = input.onPlayerReady;

  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;

    let cancelled = false;

    const boot = async () => {
      const mount = mountRef.current;
      if (!mount) return;
      // YT.Player replaces the node it is given, so it gets one React never saw.
      const host = document.createElement('div');
      mount.appendChild(host);

      try {
        const player = await youtubeIframeController({
          mount: host,
          videoId: input.youtubeId,
          showControls: true
        });
        if (cancelled) {
          player.destroy();
          return;
        }
        playerRef.current = player;
        onReadyRef.current((ms: number) => {
          player.seekToMs(ms);
          player.playVideo();
        });
      } catch {
        if (cancelled) return;
        setError('The video player could not load.');
      }
    };

    void boot();
    return () => {
      cancelled = true;
    };
  }, [input.youtubeId]);

  const toggleReplay = async () => {
    if (replayOn) {
      setReplayOn(false);
      return;
    }
    if (events) {
      setReplayOn(true);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // 15 reviewers at ~15 taps is ~225 rows (spec §6.3), so the whole stream is
      // fetched once and scheduled client-side rather than polled.
      setEvents(await getCreatorReplayStream(input.dashboardKey));
      setReplayOn(true);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not load the replay.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboardPlayer">
      <div className="dashboardPlayerFrame">
        <div className="playerMount" ref={mountRef} />
        <ReactionReplayOverlay
          enabled={replayOn}
          events={events ?? []}
          icons={input.icons}
          readCurrentTimeMs={() => playerRef.current?.currentTimeMs() ?? 0}
        />
      </div>

      <div className="dashboardPlayerControls">
        <button className="buttonSecondary" type="button" onClick={() => void toggleReplay()} disabled={loading}>
          {loading ? 'Loading…' : replayOn ? 'Turn off reaction replay' : 'Turn on reaction replay'}
        </button>
        <p className="dashboardPlayerHint">
          Plays every reviewer's reactions over the video at the moment they felt them.
          It shows you the feel of a moment, not the count — the timeline above has the numbers.
        </p>
        {error ? <div className="calloutError">{error}</div> : null}
      </div>
    </div>
  );
}
